import { useEffect, useRef, useState } from "react";
import type { IDocumentScannerHandle } from "scanbot-web-sdk/@types/interfaces/i-document-scanner-handle";
import type { DocumentDetectionResult } from "scanbot-web-sdk/@types/model/document/document-detection-result";
import { defineMessages, useIntl } from "react-intl";

import { grantedPermissions } from "util/permissions";
import { captureException } from "util/exception";
import { isiOSDevice } from "util/support";
import { useFeatureFlag } from "common/feature_gating";

type ScanbotInstance = InstanceType<typeof import("scanbot-web-sdk").default>;

function getLicenseKey(): string {
  const NOTARIZE_LICENSE_KEY =
    "mQQdLfzPLQKhdseN6RSF2NTc3qcyAHzEa1T/0huSsCyIpLf4PYoobeVCOHseyzPriYlMlNJJQ+of4QNbU+XuYVr7WFFI8ODQwR+usY8nbHU+Y+P93nwUjeW5okYfyi8VyCVt2DxptVk8BfhzPGuEHqaJpko9fq4zf6M2d2KuU0Hmm2SfVFgOE4lsDy9AxdtbFdjCR/9nJjsP6AFi1trAhOwF/eQy2rRGd0VGfUXdQGbf44CIbc8sOzskieH+UcNkm79fpm3Lx0flVdoQVT+0hpQaaOqR/5D6Hlvin7nBXkjL+WvsjMD175aLpu3jxU/pMwWjsVFnUJQm1knjUQyF6Q==\nU2NhbmJvdFNESwoqbm90YXJpemUuY29tfCpub3Rhcml6ZS5yb2Nrc3xsb2NhbGhvc3QKMTcwNTQ0OTU5OQo1OTAKOA==\n";
  const PROOF_LICENSE_KEY =
    "lA+2/WL/4UtHlS2hnPIz6UN3Nb3uL9Q58PDEiULw3o9EiGA2wAqtmxjCWLLFLlZOFbItheo5hkVyb0qMvHdgYP0sNB/E9GeRmB5ZDmUPvPFAg5mCakH/Gels6ayqAi1Mapauug1RrLL73L6EwnCG4LaWy+/F9dh/K1O3JRF9M7cQ7mfh97zegITCoraFONiDzDKtGLs+pQRSW9/4KDv3iByw7IPYCa3hV1KJOwRle7NemxvT6TqdyIHxKV1oU/yB4WVQ2R1nWiklJ+nQkaFIgLIDcTm4uAVX0is/1zvhCqHjNTBxju/2ixyQiQ5M2v/3uAtGW2LD845lOeQM7NOALg==\nU2NhbmJvdFNESwoqcHJvb2YuY29tfCpwcm9vZi5jb2FjaHxsb2NhbGhvc3QKMTcwMjc3MTE5OQo1OTAKOA==\n";
  const { hostname } = window.location;
  if (hostname.endsWith("proof.com") || hostname.endsWith("proof.coach")) {
    return PROOF_LICENSE_KEY;
  }
  return NOTARIZE_LICENSE_KEY;
}

function useLazyScanbot() {
  const [instance, setInstance] = useState<ScanbotInstance>();
  useEffect(() => {
    import("scanbot-web-sdk").then(async (mod) => {
      const scanbotInstance = await mod.default.initialize({
        licenseKey: getLicenseKey(),
      });
      setInstance(scanbotInstance);
    });
  }, []);
  return instance;
}

// Cannot directly import enum from "scanbot-web-sdk/@types/model/document/detection-status"
// since its in a .d.ts so it won't have a compiled output. Copying over to have our own declaration
export enum DetectionStatus {
  NotAcquired = "NotAcquired",
  OK = "OK",
  // eslint-disable-next-line @typescript-eslint/naming-convention
  OK_SmallSize = "OK_SmallSize",
  // eslint-disable-next-line @typescript-eslint/naming-convention
  OK_BadAngles = "OK_BadAngles",
  // eslint-disable-next-line @typescript-eslint/naming-convention
  OK_BadAspectRatio = "OK_BadAspectRatio",
  // eslint-disable-next-line @typescript-eslint/naming-convention
  OK_OffCenter = "OK_OffCenter",
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Error_NothingDetected = "Error_NothingDetected",
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Error_Brightness = "Error_Brightness",
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Error_Noise = "Error_Noise",
}

export type DocumentScannerError = "media_permission_error" | "unknown_error";

export type CaptureResult = { imageBuffer: ArrayBuffer; dataUrl: string };

type DocScannerCaptureState =
  | { state: "loading" }
  | { state: "error"; error: DocumentScannerError }
  | { state: "ready" };
type OnCapture = (
  result:
    | ({ success: true } & CaptureResult)
    | { success: false; detectionStatus: DetectionStatus },
) => void;
export type UseDocScannerCapture = (args: {
  containerId: string;
  onCapture: OnCapture;
}) => DocScannerCaptureState;

type RotateImage = (captureResult: CaptureResult) => Promise<CaptureResult>;
type GeneratePdf = (
  captureResults: CaptureResult[],
) => Promise<{ blob: BlobPart; filename: string }>;

type DocScannerState =
  | { state: "loading" }
  | { state: "error" }
  | {
      state: "ready";
      useDocScannerCapture: UseDocScannerCapture;
      rotateImage: RotateImage;
      generatePdf: GeneratePdf;
    };

const messages = defineMessages({
  OK: {
    id: "190b41af-cb29-439e-ab13-721020f70964",
    defaultMessage: "Capturing your document... Please do not move the camera.",
  },
  OK_SmallSize: {
    id: "665b7e2f-95bb-4c8a-a6d3-ee76c78568d2",
    defaultMessage: "The document is too small. Try moving closer.",
  },
  OK_BadAngles: {
    id: "1dcbc271-d0bc-46cd-8c3c-2ccb4e09357a",
    defaultMessage: "This is a bad camera angle. Hold the device straight over the document.",
  },
  OK_BadAspectRatio: {
    id: "fc0fde8f-72e0-4b95-accd-48e0cbf5c1a0",
    defaultMessage: "Rotate the device sideways, so that the document fits better into the screen.",
  },
  OK_OffCenter: {
    id: "159738ba-c280-4a23-9c0e-954518ffa3b5",
    defaultMessage: "Try holding the device at the center of the document.",
  },
  Error_NothingDetected: {
    id: "02ab81a4-5fb8-4512-8a30-8d50833cef08",
    defaultMessage: "Please hold the device over a document to start scanning.",
  },
  Error_Brightness: {
    id: "ab1aea3a-8a32-497b-9c08-353ff4991af1",
    defaultMessage: "It is too dark. Try turning on a light.",
  },
  Error_Noise: {
    id: "9e556d17-3de5-4095-8f49-22146c8ce859",
    defaultMessage:
      "Place your document on a solid, non-reflective background in a well-lit space.",
  },
});

export function useDocScanner() {
  const scanbotInstance = useLazyScanbot();
  const [scannerState, setScannerState] = useState<DocScannerState>({ state: "loading" });

  useEffect(() => {
    if (scanbotInstance) {
      scanbotInstance
        .getLicenseInfo()
        .then((licenseInfo) => {
          if (!licenseInfo.isValid()) {
            setScannerState({ state: "error" });
            return;
          }

          const useDocScannerCapture = ({
            containerId,
            onCapture,
          }: {
            containerId: string;
            onCapture: OnCapture;
          }) => {
            const intl = useIntl();
            const enableScanbotAutoCapture = useFeatureFlag("enable_scanbot_auto_capture", false);
            const scannerHandle = useRef<IDocumentScannerHandle>();
            const [captureState, setCaptureState] = useState<DocScannerCaptureState>({
              state: "loading",
            });

            const checkGrantedPermissions = () => {
              grantedPermissions().then((permissions) => {
                const { webcam } = permissions;
                if (!webcam) {
                  setCaptureState({ state: "error", error: "media_permission_error" });
                }
              });
            };

            // permissions.query is not available on all versions of Safari
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (navigator?.permissions?.query) {
              navigator.permissions
                .query({ name: "camera" as unknown as PermissionName })
                .then((status) => {
                  // onchange not (yet) available on safari so can only rely on rerender when shutter button clicked
                  if (isiOSDevice()) {
                    if (status.state !== "granted") {
                      setCaptureState({ state: "error", error: "media_permission_error" });
                    }
                  } else {
                    status.onchange = () => {
                      if (status.state !== "granted") {
                        setCaptureState({ state: "error", error: "media_permission_error" });
                      }
                    };
                  }
                })
                .catch(() => {
                  checkGrantedPermissions();
                });
            } else {
              checkGrantedPermissions();
            }

            useEffect(() => {
              scanbotInstance
                .createDocumentScanner({
                  onError: captureException,
                  containerId,
                  autoCaptureEnabled: enableScanbotAutoCapture,
                  onDocumentDetected: async (result: DocumentDetectionResult) => {
                    if (result.cropped && result.detectionStatus === DetectionStatus.OK) {
                      scanbotInstance.utils.flash();
                      const imageBuffer = result.cropped;
                      onCapture({
                        success: true,
                        imageBuffer,
                        dataUrl: await scanbotInstance.toDataUrl(imageBuffer),
                      });
                    } else if (!enableScanbotAutoCapture) {
                      scanbotInstance.utils.flash();
                      onCapture({
                        success: false,
                        detectionStatus:
                          result.detectionStatus || DetectionStatus.Error_NothingDetected,
                      });
                    }
                  },
                  text: {
                    hint: {
                      OK: intl.formatMessage(messages.OK),
                      OK_SmallSize: intl.formatMessage(messages.OK_SmallSize),
                      OK_BadAngles: intl.formatMessage(messages.OK_BadAngles),
                      OK_BadAspectRatio: intl.formatMessage(messages.OK_BadAspectRatio),
                      OK_OffCenter: intl.formatMessage(messages.OK_OffCenter),
                      Error_NothingDetected: intl.formatMessage(messages.Error_NothingDetected),
                      Error_Brightness: intl.formatMessage(messages.Error_Brightness),
                      Error_Noise: intl.formatMessage(messages.Error_Noise),
                    },
                  },
                })
                .then((handle) => {
                  scannerHandle.current = handle;
                  setCaptureState({ state: "ready" });
                })
                .catch((error) => {
                  if (error.name === "MediaPermissionError") {
                    setCaptureState({ state: "error", error: "media_permission_error" });
                  } else {
                    setCaptureState({ state: "error", error: "unknown_error" });
                  }
                  captureException(error);
                });

              return () => {
                scannerHandle.current?.dispose();
              };
            }, []);

            return captureState;
          };

          const rotateImage: RotateImage = async (captureResult) => {
            const rotatedImageBuffer = await scanbotInstance.rotateImageCcw(
              captureResult.imageBuffer,
              3,
            );
            return {
              imageBuffer: rotatedImageBuffer,
              dataUrl: await scanbotInstance.toDataUrl(rotatedImageBuffer),
            };
          };

          const generatePdf: GeneratePdf = async (captureResults) => {
            const pdfGenerator = await scanbotInstance.beginPdf({
              standardPaperSize: "Letter",
            });
            await Promise.all(
              captureResults.map(async (captureResult) => {
                await pdfGenerator.addPage(captureResult.imageBuffer);
              }),
            );
            return {
              blob: await pdfGenerator.complete(),
              filename: `scanned ${new Date().toLocaleString()}.pdf`,
            };
          };

          setScannerState({
            state: "ready",
            useDocScannerCapture,
            rotateImage,
            generatePdf,
          });
        })
        .catch((error) => {
          captureException(error);
          setScannerState({ state: "error" });
        });
    }
  }, [scanbotInstance]);

  return scannerState;
}
